import { useEffect } from 'react';
import _ from 'lodash';
import { useHistory, useParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import useFetch from '~/view-data/use-fetch';
import useGlobalContext from '~/view-data/use-global-context';
import useHeader from '~/view-data/use-header';
import { showMessage } from '~/helper';
import api from '~/services/api';

const initialState = {
  loading: false,
  modalLoading: false,
  showEmployeeModal: false,
  showCredentialModal: false,
  showCovidModal: false,
  data: [],
};

const useEventMatchday = () => {
  const history = useHistory(),
    { id, mode } = useParams(),
    { state: { eventMatchday = initialState }, dispatch } = useGlobalContext(),
    { state: headerState, actions: headerActions } = useHeader();
  
  const failure = (error) => {
    dispatch({ type: 'eventMatchday', payload: { loading: false, modalLoading: false } });
    showMessage('error', error);
  };
  let extraParams = {}
  if (mode === 'running') {
    extraParams.refreshInterval = 5000;
  }
  const { data, mutate, error } = useFetch({ url: 'events/load-summary', params: { id, status: mode } }, extraParams);

  if (error) {
    failure(error);
    history.push('/events');
  }
  useEffect(() => {
    headerActions.configure({
      title: `Evento (#${id}) - ${_.get(data, 'event.name') || ''}` ,
      loading: eventMatchday.loading
    });
    // eslint-disable-next-line
  }, [eventMatchday.loading, headerState.filter.data, data]);

  const goToEvents = () => {
    history.push('/events');
  };

  const exportPdf = async () => {
    dispatch({ type: 'eventMatchday', payload: { loading: true } });
    let date = _.get(data, 'event.eventDate');
    date = (date) ? parseISO(date) : new Date();
    try {
      let response = await api.get(`events/export-xlsx`, { responseType: 'blob', params: { id } }),
        filename = `evento-${format(date, 'dd-MM-yyyy')}.xlsx`;
      api.download(response, filename);

      dispatch({ type: 'eventMatchday', payload: { loading: false } });
    } catch (error) {
      failure(error);
    }
  };

  const finish = async ({ observation }) => {
    dispatch({ type: 'eventMatchday', payload: { loading: true } });

    try {
      await api.put(`events/mark-finish`, { id, status: mode, observation });
      showMessage('success', 'Evento finalizado com sucesso!');
      history.push('/events');
      dispatch({ type: 'eventMatchday', payload: { loading: false } });
    } catch (error) {
      failure(error);
    }
  };

  const openEmployeeModal = (modalData) => {
    dispatch({ type: 'eventMatchday', payload: { modalData, showEmployeeModal: true }});
  };

  const closeEmployeeModal = () => {
    dispatch({ type: 'eventMatchday', payload: { modalData: {}, showEmployeeModal: false }});
  };

  const saveEmployeeModal = async (params) => {
    try {
      dispatch({ type: 'eventMatchday', payload: { modalLoading: true } });
      await api.put(`events/matchday-save-person`, { eventId: id, ...params.employee});
      mutate(data);
      dispatch({ type: 'eventMatchday', payload: { modalLoading: false, modalData: {}, showEmployeeModal: false } });
      showMessage('success', `Colaborador alterado com sucesso!`);
    } catch (error) {
      failure(error);
    }
  };

  const removeEmployee = async (params) => {
    try {
      dispatch({ type: 'eventMatchday', payload: { loading: true }});
      await api.post(`events/matchday-remove-person`, { eventId: id, ..._.pick(params, 'personId', 'companyId') });
      mutate(data);
      dispatch({ type: 'eventMatchday', payload: { loading: false }});
      showMessage('success', `Colaborador removido com sucesso!`);
    } catch (error) {
      failure(error);
    }
  };

  const isExpanded = (row) => {
    let expanded = eventMatchday.expanded;
    return expanded === row.id;
  };

  const setExpanded = (state, row) => {
    let expanded = (state) ? row.id : null;
    dispatch({ type: 'eventMatchday', payload: { expanded } });
  };

  const listPersons = async (term, supplier, callback) => {
    try {
      let companyId = _.get(supplier, 'id');
      const response = await api.get(`events/list-persons`, { params: { term, companyId } });
      callback(response.data);
    } catch (error) {
      failure(error);
    }
  };

  const listRoles = async (term, callback) => {
    try {
      const response = await api.get(`events/list-roles`, { params: { term } });
      callback(response.data);
    } catch (error) {
      failure(error);
    }
  };

  const listSuppliers = async (term, callback) => {
    try {
      const response = await api.get(`events/list-suppliers`, { params: { term } });
      callback(response.data);
    } catch (error) {
      failure(error);
    }
  };

  const openCredentialModal = (supplier) => {
    let modalData = { emails: _.get(supplier, 'email') || '', supplier };
    dispatch({ type: 'eventMatchday', payload: { showCredentialModal: true, modalData }});
  };

  const closeCredentialModal = () => {
    dispatch({ type: 'eventMatchday', payload: { showCredentialModal: false, modalData: {} }});
  };

  const createCredentials = async (supplier, mode, emails) => {
    let params = { id: id, companyId: _.get(supplier, 'id'), mode, emails },
      filename = `credenciais-${_.get(supplier, 'identify')}-${format(new Date(), 'yyyy-MM-dd')}.pdf`,
      isDownload = mode === 'download';

    try {
      dispatch({ type: 'eventMatchday', payload: { modalLoading: true } });
      let response = await api.get(`events/create-credentials`, { params, responseType: 'blob' });

      if (isDownload) {
        api.download(response, filename);
      }
      dispatch({ type: 'eventMatchday', payload: { showCredentialModal: false, modalLoading: false, modalData: {} } });

      showMessage('success', `Credenciais geradas com sucesso!`);
    } catch (error) {
      failure(error);
    }
  };

  const openCovidModal = (modalData) => {
    const  person = _.get(modalData, "person") || {};
    dispatch({
      type: "eventMatchday",
      payload: { modalPCovidData: { employee: { person } }, showCovidModal: true },
    });
  };

  const closeCovidModal = () => {
    dispatch({
      type: "eventMatchday",
      payload: { modalPCovidData: {}, showCovidModal: false },
    });
  };

  return {
    state: {
      ...eventMatchday,
      loading: !data,
      data,
      mode
    },
    actions: {
      goToEvents,
      exportPdf,
      finish,
      isExpanded,
      setExpanded,
      listRoles,
      listPersons,
      listSuppliers,
      openEmployeeModal,
      closeEmployeeModal,
      saveEmployeeModal,
      removeEmployee,
      createCredentials,
      openCredentialModal,
      closeCredentialModal,
      openCovidModal,
      closeCovidModal,
    },
  };
};

export default useEventMatchday;
